import { Injectable } from '@angular/core';
import {
  Auth,
  AuthProvider,
  GoogleAuthProvider,
  IdTokenResult,
  UserCredential,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateEmail
} from '@angular/fire/auth';
import { ILoginCredentials } from '../models/login-credentials.interface';
import { IRegisterResult } from '../models/register-result.interface';
import { HttpService } from './http.service';
import { EndpointsService } from './endpoints.service';
import { v6 as uuidv6 } from 'uuid';
import { Storage } from '@ionic/storage-angular';
import { collectionData, collectionGroup, Firestore, query, where, deleteDoc, doc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { isPlatform, NavController, Platform } from '@ionic/angular';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private initializedApp: boolean = false;
  private initialized = false;

  get uid() {
    if (this.auth.currentUser) {
      return this.auth.currentUser.uid;
    }
    return '';
  }

  get email() {
    if (this.auth.currentUser) {
      return this.auth.currentUser.email;
    }
    return '';
  }

  constructor(
    private auth: Auth,
    private httpService: HttpService,
    private enpointService: EndpointsService,
    private storage: Storage,
    private firestore: Firestore,
    private router: Router,
    private navCtrl: NavController,
    private readonly platform: Platform,
  ) {
    this.init();
  }

  async init() {
    this.storage.create();

    if (await this.getGuestFlag()) {
      this.initializedApp = true;
    }

    if (!this.initializedApp) {
      this.initializedApp = true;
      this.router.navigateByUrl('');
    }
  }

  getGuestFlag(): Promise<boolean> {
    return this.storage.get('guest_flag');
  }

  getGuestUuid(): Promise<string> {
    return this.storage.get('guest_uuid')
  }

  getCurrentToken(): Promise<IdTokenResult> {
    return this.auth.currentUser!.getIdTokenResult();
  }

  async register(registration: ILoginCredentials): Promise<IRegisterResult> {
    try {
      await createUserWithEmailAndPassword(this.auth, registration.email, registration.password);
      if (this.auth.currentUser) {
        await sendEmailVerification(this.auth.currentUser);
        return {
          success: true
        }
      } else {
        return {
          success: false,
          errorCode: 'Cannot initialize session'
        }
      }
    } catch (e: any) {
      return {
        success: false,
        errorCode: e['code'],
      }
    }
  }

  async createCard(businessId: string): Promise<any> {
    const promise = new Promise<any>((resolve) => {
      let card = {
        userId: this.uid,
        businessId: businessId,
        description: "Tarjeta Baguu",
        currency: "MXN",
        status: "enabled",
        balance: 0.00,
        userTransactionId: this.uid,
        mitId: "NA",
        amount: 0.00
      }

      this.httpService.post(this.enpointService.createCard, card).subscribe({
        next: (data: any) => {
          resolve(data);
        }, error: (error: any) => {
          console.error('Error en la solicitud HTTP:', error);
          resolve(null);
        }
      });
    });

    return promise;
  }

  async sendPasswordResetEmail(email: string) {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (e) {
      return false
    }
    return true
  }

  async resendEmailVerification() {
    try {
      if (this.auth.currentUser) {
        await sendEmailVerification(this.auth.currentUser);
      } else {
        return false;
      }
    } catch (e) {
      return false
    }
    return true
  }

  async login(credentials: ILoginCredentials) {
    try {
      await signInWithEmailAndPassword(this.auth, credentials.email, credentials.password);
      return this.auth.currentUser;
    } catch (e) {
      return null;
    }
  }

  async loginAsGuest() {
    const flag = await this.getGuestFlag();
    if (flag) {
      const uuid = await this.getGuestUuid();
      return uuid;
    } else {
      let uuid = uuidv6();
      this.storage.set('guest_uuid', uuid);
      this.storage.set('guest_flag', true);
      return uuid;
    }
  }

  async logoutGuest() {
    await this.storage.remove('guest_uuid');
    await this.storage.remove('guest_flag');
    await this.storage.remove('guest_zip_code');
    await this.storage.remove('guest_position');
  }

  async logout() {
    await signOut(this.auth);
  }

  async updateEmail(email: string): Promise<IRegisterResult> {
    try {
      if (this.auth.currentUser) {
        await updateEmail(this.auth.currentUser, email);
        await sendEmailVerification(this.auth.currentUser);
        return {
          success: true,
        }
      } else {
        return {
          success: false,
          errorCode: 'There is not session initialized',
        }
      }
    } catch (e: any) {
      return {
        success: false,
        errorCode: e['code'],
      }
    }
  }

  findUserProfile(ownerId: string, isAdmin: boolean): Promise<any> {
    const promise = new Promise<boolean>((resolve, reject) => {
      console.log('findUserProfile :::::::::>>>>> ', ownerId);
      let profile = '';
      if (isAdmin) {
        profile = 'adminProfile';
      } else {
        profile = 'businessProfile';
      }
      const profiles = query(
        collectionGroup(this.firestore, profile),
        where('registrationId', '==', ownerId)
      );

      collectionData(profiles, { idField: 'registrationId' }).subscribe(
        (data: any) => {
          resolve(data);
        }
      );
    });

    return promise;
  }



  async deleteAccount(ownerId: string) {

    this.auth.currentUser?.delete().then(function () {
      console.log("Usuario eliminado");

    }).catch(function (error) {
      console.log("Nopude eliminar el usuario, intenta mas tarde");
    });

    deleteDoc(doc(this.firestore, 'userProfile', ownerId));
    setTimeout(function () { window.location.replace('/'); }, 3000);

  }

  signInWithGoogleProvider(): Promise<UserCredential> {
    return this.callPopUp();
  }

  async callPopUp(): Promise<any> {
    try {
      const result = await this.nativeGoogleLogin();
      return result;
    } catch (error: any) {
      return error;
    }
  }

  private async nativeGoogleLogin(): Promise<any> {
    const googleUser = await GoogleAuth.signIn();
    if (!googleUser || !googleUser.authentication?.idToken) {
      throw new Error('No se pudo obtener el idToken de Google');
    }
    const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
    const userCredential = await signInWithCredential(this.auth, credential);
    return userCredential;
  }

  async registerWithGoogle(): Promise<IRegisterResult> {
    try {
      if (this.auth.currentUser) {
        await sendEmailVerification(this.auth.currentUser);
        return {
          success: true
        }
      } else {
        return {
          success: false,
          errorCode: 'Cannot initialize session'
        }
      }
    } catch (e: any) {
      return {
        success: false,
        errorCode: e['code'],
      }
    }
  }

  async initializeGoogleAuth() {
    if (!this.initialized) {
      if (isPlatform("ios")) {
        await GoogleAuth.initialize({
          clientId: '497970479609-9gvb7i1o222viomlt3vel13e5lb701cn.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true
        });
        this.initialized = true;
      } else {
        await GoogleAuth.initialize({
          clientId: '497970479609-mh16v6jh70812a3psmbrg833isp27auo.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true
        });
        this.initialized = true;
      }
    }
  }
}
